<script lang="ts" setup>
import { ref, inject, watch, computed, onMounted } from 'vue';

import type { Ref } from 'vue';
import type { Viewer, Nullable } from '@immersive-tech/viewer-engine';

type CanvasWidth = number;
type CanvasHeight = number;
type CanvasSize = [CanvasWidth, CanvasHeight];

interface ISizeOption {
  label: string;
  width: CanvasWidth;
  height: CanvasHeight;
}

const sizeOptions: ISizeOption[] = [
  { label: 'Full', width: 0, height: 0 },
  { label: 'iPhone SE', width: 375, height: 667 },
  { label: 'iPhone XR', width: 414, height: 896 },
  { label: 'iPhone 12 Pro', width: 390, height: 844 },
  { label: 'Pixel 5', width: 393, height: 851 },
  { label: 'Samsung Galaxy S8+', width: 360, height: 740 },
  { label: 'Samsung Galaxy S20 Ultra', width: 412, height: 915 },
  { label: 'iPad Air', width: 820, height: 1180 },
  { label: 'iPad Mini', width: 768, height: 1024 },
  { label: 'Surface Pro 7', width: 912, height: 1368 },
  { label: 'Surface Duo', width: 540, height: 720 },
  { label: 'Samsung Galaxy A51/71', width: 412, height: 914 },
  { label: 'Custom', width: 0, height: 0 },
];

const canvasElement = inject('canvasElement') as Ref<Nullable<HTMLCanvasElement>>;
const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const originalSize = ref<CanvasSize>([canvasElement.value?.getBoundingClientRect().width ?? 0, canvasElement.value?.getBoundingClientRect().height ?? 0]);
const canvasContainerResizeObserver = ref<Nullable<ResizeObserver>>(null);
const selectedSizeIndex = ref(0);
const customWidth = ref<CanvasWidth>(canvasElement.value?.getBoundingClientRect().width ?? 0);
const customHeight = ref<CanvasHeight>(canvasElement.value?.getBoundingClientRect().height ?? 0);

const selectedSize = computed<CanvasSize>((): CanvasSize => {
  if (sizeOptions[selectedSizeIndex.value].label === 'Custom') {
    return [customWidth.value, customHeight.value];
  } else if (sizeOptions[selectedSizeIndex.value].label === 'Full') {
    return originalSize.value;
  }

  return [sizeOptions[selectedSizeIndex.value].width, sizeOptions[selectedSizeIndex.value].height];
});

const updateCanvasWidth = (newWidth: number) => {
  if (!canvasElement.value) return;
  if (canvasElement.value.width === newWidth) return;

  canvasElement.value.style.width = `${newWidth}px`;
};

const updateCanvasHeight = (newHeight: number) => {
  if (!canvasElement.value) return;
  if (canvasElement.value.height === newHeight) return;

  canvasElement.value.style.height = `${newHeight}px`;
};

const canvasContainerResizehandler = (entries: ResizeObserverEntry[]) => {
  if (!canvasElement.value) return;

  originalSize.value = [entries[0].contentRect.width, entries[0].contentRect.height];
};

const mountHandler = () => {
  if (!canvasElement.value) return;
  if (!canvasElement.value.parentElement) return;

  const [originalWidth, originalHeight] = originalSize.value;

  canvasContainerResizeObserver.value = new ResizeObserver(canvasContainerResizehandler);
  canvasContainerResizeObserver.value.observe(canvasElement.value.parentElement);

  canvasElement.value.width = originalWidth;
  canvasElement.value.height = originalHeight;

  viewer.value?.resize();
};

const selectedSizeWatcher = ([newWidth, newHeight]: [number, number]) => {
  updateCanvasHeight(newHeight);
  updateCanvasWidth(newWidth);

  if (viewer.value) viewer.value.resize();
};

onMounted(mountHandler);
watch(selectedSize, selectedSizeWatcher);
</script>

<template>
  <div class="flex flex-col absolute bottom-[5px] left-[5px] border justify-center items-center text-xs font-mono bg-white/50 leading-none">
    <p class="py-0.5">
      Canvas Size
    </p>

    <select v-model="selectedSizeIndex" class="py-0.5 w-full">
      <option v-for="(option, index) in sizeOptions" :key="`${option.label}.${index}`" :value="index">
        {{ option.label }}
      </option>
    </select>

    <div v-if="sizeOptions[selectedSizeIndex].label === 'Custom'" class="flex flex-row items-center border-t">
      <div class="flex flex-col border-r p-0.5">
        <p class="font-bold">
          Width
        </p>
        <div class="flex flex-row flex-shrink border p-0.5">
          <input v-model="customWidth" class="w-[75px] bg-white" step="0.0001" min="1" type="number">
          <p class="border-l bg-white">
            px
          </p>
        </div>
      </div>
      <div class="flex flex-col p-0.5">
        <p class="font-bold">
          Height
        </p>
        <div class="flex flex-row border p-0.5">
          <input v-model="customHeight" class="w-[75px] bg-white" step="0.0001" min="1" type="number">
          <p class="border-l bg-white">
            px
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
